// Protected surface: review docs/protected-surfaces/session-pwa-transport.md before changing session-ready bootstrap or refresh coordination behavior.
/**
 * Global Authentication Manager
 * 
 * Implements sequential auth flow and single-flight refresh to prevent
 * authentication loops and refresh storms on mobile apps.
 * 
 * Key features:
 * 1. sessionReady flag - gates all authenticated requests
 * 2. Single-flight refresh - one global refresh promise shared by all callers
 * 3. Sequential bootstrap - ensures auth is ready before allowing requests
 */

import { safeRemoveItem } from './storageSafe';

// Global state
let sessionReady = false;
let sessionReadyListeners = [];
let refreshPromise = null;
let bootstrapInProgress = false;
let bootstrapPromise = null;
const AUTH_MANAGER_DEBUG = String(process.env.REACT_APP_AUTH_FLOW_DEBUG || '').trim() === '1';
const authManagerDebug = (...args) => {
  if (AUTH_MANAGER_DEBUG) window.console.log(...args);
};

/**
 * Initialize session - must be called on app bootstrap
 * Sequential flow: refresh → loadMe → sessionReady
 */
export async function bootstrapSession(authApiClient) {
  // Prevent multiple bootstrap attempts
  if (bootstrapInProgress) {
    authManagerDebug('[authManager] Bootstrap already in progress, waiting...');
    return bootstrapPromise;
  }

  if (sessionReady) {
    authManagerDebug('[authManager] Session already ready, skipping bootstrap');
    return { success: true, user: null };
  }

  bootstrapInProgress = true;
  bootstrapPromise = (async () => {
    try {
      authManagerDebug('[authManager] 🚀 Starting session bootstrap...');

      // Step 1: Try to refresh token ONCE
      const refreshResult = await tryRefreshOnce(authApiClient);
      
      if (!refreshResult.success) {
        authManagerDebug('[authManager] ❌ Refresh failed, session not ready:', refreshResult.error);
        setSessionReady(false);
        return { success: false, error: refreshResult.error, code: refreshResult.code };
      }

      // Step 2: Verify session by loading user
      try {
        const meResponse = await authApiClient.get('/users/me');
        const user = meResponse.data;
        
        authManagerDebug('[authManager] ✅ Session verified, user loaded:', user.username);
        setSessionReady(true);
        
        return { success: true, user };
      } catch (error) {
        console.error('[authManager] ❌ Failed to load user after refresh:', error);
        setSessionReady(false);
        return { 
          success: false, 
          error: error.response?.data?.message || 'Failed to verify session',
          code: error.response?.data?.code || 'SESSION_VERIFICATION_FAILED'
        };
      }
    } catch (error) {
      console.error('[authManager] ❌ Bootstrap failed:', error);
      setSessionReady(false);
      return { 
        success: false, 
        error: error.message || 'Bootstrap failed',
        code: 'BOOTSTRAP_ERROR'
      };
    } finally {
      bootstrapInProgress = false;
      bootstrapPromise = null;
    }
  })();

  return bootstrapPromise;
}

/**
 * Try to refresh token once - single call, no retries
 */
async function tryRefreshOnce(authApiClient) {
  try {
    authManagerDebug('[authManager] Attempting token refresh...');
    
    // Check if rate limited
    if (typeof window !== 'undefined' && window.__RATE_LIMITED) {
      const rateLimitUntil = window.__RATE_LIMIT_UNTIL || 0;
      if (Date.now() < rateLimitUntil) {
        const remainingMs = rateLimitUntil - Date.now();
        console.warn(`[authManager] ⏸️ Still rate limited for ${Math.ceil(remainingMs / 1000)}s`);
        return { 
          success: false, 
          error: 'Rate limited', 
          code: 'RATE_LIMITED',
          retryAfter: Math.ceil(remainingMs / 1000)
        };
      } else {
        // Rate limit expired, clear flag
        window.__RATE_LIMITED = false;
        window.__RATE_LIMIT_UNTIL = 0;
      }
    }

    const response = await authApiClient.post('/refresh_tokens', {}, { withCredentials: true });
    
    if (response.status === 200) {
      authManagerDebug('[authManager] ✅ Token refresh successful');
      return { success: true, data: response.data };
    } else {
      return { success: false, error: 'Refresh failed', code: 'REFRESH_FAILED' };
    }
  } catch (error) {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    
    // Handle rate limit
    if (status === 429) {
      if (typeof window !== 'undefined') {
        window.__RATE_LIMITED = true;
        window.__RATE_LIMIT_UNTIL = Date.now() + (15 * 60 * 1000); // 15 minutes
      }
      return { 
        success: false, 
        error: 'Too many refresh attempts', 
        code: code || 'REFRESH_RATE_LIMIT_EXCEEDED',
        retryAfter: 15 * 60
      };
    }

    // Handle missing refresh token
    if (status === 401 || code === 'NO_REFRESH_TOKEN') {
      return { 
        success: false, 
        error: 'No refresh token available', 
        code: 'NO_REFRESH_TOKEN'
      };
    }

    // Handle expired refresh token
    if (status === 403 || code === 'REFRESH_TOKEN_EXPIRED') {
      return { 
        success: false, 
        error: 'Refresh token expired', 
        code: 'REFRESH_TOKEN_EXPIRED'
      };
    }

    return { 
      success: false, 
      error: error.response?.data?.message || error.message, 
      code: code || 'REFRESH_ERROR'
    };
  }
}

/**
 * Get single-flight refresh promise
 * All callers share the same refresh attempt
 */
export function getRefreshPromise(authApiClient) {
  // If refresh already in flight, return existing promise
  if (refreshPromise) {
    authManagerDebug('[authManager] Refresh already in flight, reusing promise');
    return refreshPromise;
  }

  // Start new refresh
  authManagerDebug('[authManager] Starting new refresh attempt');
  refreshPromise = (async () => {
    try {
      const result = await tryRefreshOnce(authApiClient);
      
      if (result.success) {
        // Refresh succeeded, session should be ready
        setSessionReady(true);
      } else {
        // Refresh failed - clear session if it's a permanent failure
        if (result.code === 'NO_REFRESH_TOKEN' || result.code === 'REFRESH_TOKEN_EXPIRED') {
          setSessionReady(false);
        }
      }
      
      return result;
    } finally {
      // Clear promise so next refresh can start
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Wait for session to be ready
 * Useful for gating requests until auth is established
 */
export function waitForSessionReady() {
  if (sessionReady) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    sessionReadyListeners.push(resolve);
  });
}

/**
 * Check if session is ready
 */
export function isSessionReady() {
  return sessionReady;
}

/**
 * Set session ready state and notify listeners
 */
function setSessionReady(ready) {
  const wasReady = sessionReady;
  sessionReady = ready;

  if (wasReady !== ready) {
    authManagerDebug(`[authManager] Session ready state changed: ${wasReady} → ${ready}`);
    
    // Notify all listeners
    const listeners = [...sessionReadyListeners];
    sessionReadyListeners = [];
    listeners.forEach(listener => listener(ready));
  }
}

/**
 * Reset session (on logout)
 */
export function resetSession() {
  authManagerDebug('[authManager] Resetting session');
  sessionReady = false;
  refreshPromise = null;
  bootstrapInProgress = false;
  bootstrapPromise = null;
  sessionReadyListeners = [];
  
  // Clear global flags
  if (typeof window !== 'undefined') {
    window.__RATE_LIMITED = false;
    window.__RATE_LIMIT_UNTIL = 0;
    window.__LOGGED_OUT = true;
  }
  
  safeRemoveItem('fusesoul_remember_me');
}

/**
 * Force session ready (for login/signup after tokens are set)
 */
export function forceSessionReady() {
  authManagerDebug('[authManager] Force setting session ready');
  setSessionReady(true);
}
